import React, { useEffect, useMemo, useState } from 'react';
import styles from './index.module.less';
import { Affix, Skeleton } from 'antd';
import { ArticleDetailModel, HomeArticleModel } from '@/types/home';
import { imgUrl } from '@/shared/utils';
import useScreenSize from '@/lib/hooks/utils/useScreenSize';
import { useRouter } from 'next/router';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import { CommonBaseContent } from '@/components/Layouts/CommonBackContent';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import { AppSettings } from '@/shared/app-common';
import dayjs from 'dayjs';

const BookItem = dynamic(() => import('../BookItem'), { ssr: true });
const SharePage = dynamic(() => import('@/components/Common/Share'), { ssr: true });
const RightSideContent = dynamic(() => import('../RightSideContent'), { ssr: true });
const ArticleVideoContent = dynamic(() => import('../ArticleVideoContent'), { ssr: false });
// const IntroductionContent = dynamic(() => import('../IntroductionContent'), { ssr: true });
const BodyContent = dynamic(() => import('../BodyContent'), { ssr: true });
const ArticleListContent = dynamic(() => import('../ArticleListContent'), { ssr: false });
const BottomFlexButton = dynamic(() => import('../BottomFlexButton'), { ssr: false });

const ArticleDetailUI = ({
  model,
  contentData,
  recommendTags,
}: {
  model?: ArticleDetailModel;
  contentData?: any;
  recommendTags?: [HomeArticleModel];
}) => {
  const { t } = useTranslation();
  const router = useRouter();
  const { width } = useScreenSize();
  const currentHref = useMemo(() => (typeof window !== 'undefined' ? window.location.href : ''), []);
  // const { onShowArticleBottomAlert, isMobile } = useConfigProvider();

  const [isManualScroll, setIsManualScroll] = useState(false);

  useEffect(() => {
    const handleWheel = (event: WheelEvent) => {
      setIsManualScroll(true);
    };

    const handleTouchMove = (event: TouchEvent) => {
      setIsManualScroll(true);
    };

    window.addEventListener('wheel', handleWheel);
    window.addEventListener('touchmove', handleTouchMove);

    return () => {
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('touchmove', handleTouchMove);
    };
  }, []);

  const LoadingSkeleton = useMemo(() => {
    return (
      <>
        <Skeleton active />
        <Skeleton.Image active style={{ marginTop: '20px', width: '100%', aspectRatio: '16/9' }} />
        <Skeleton active style={{ marginTop: '20px' }} />
      </>
    );
  }, []);

  const [processedContent, setProcessedContent] = useState({ updatedHtml: '', h2Contents: [] });

  useEffect(() => {
    if (typeof window !== 'undefined' && contentData?.body) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(contentData.body, 'text/html');

      const h2Elements = doc.querySelectorAll('h2');
      const h2Contents: string[] = [];

      h2Elements.forEach((h2, index) => {
        h2Contents.push(h2.textContent || '');
        h2.id = `${index + 1}`;
        h2.style.scrollMarginTop = '80px';
      });

      const imgElements = doc.querySelectorAll('img');
      imgElements?.forEach((img) => {
        if (!img.hasAttribute('loading')) {
          img.setAttribute('loading', 'lazy');
        }

        const originalSrc = img.getAttribute('src');
        const resizeType = width > 576 ? 'big' : 'mid';
        if (originalSrc) {
          img.setAttribute('src', imgUrl(originalSrc, resizeType));
        }
      });

      const updatedHtml = doc.body.innerHTML;
      setProcessedContent({ updatedHtml, h2Contents });
    }
  }, [contentData?.body, width]);

  // 检测表格是否需要滚动
  useEffect(() => {
    if (typeof window !== 'undefined' && processedContent.updatedHtml) {
      const checkTableFit = () => {
        const tables = document.querySelectorAll(`.${styles.homeContent} table`);
        tables.forEach((table) => {
          const container = table.closest(`.${styles.homeContent}`);
          if (container && table instanceof HTMLElement) {
            // 计算列数
            const firstRow = table.querySelector('tr');
            const columnCount = firstRow?.querySelectorAll('th, td').length || 0;

            // 获取容器宽度
            const containerWidth = (container as HTMLElement).offsetWidth;

            // 计算平均列宽（容器宽度 / 列数）
            const averageColumnWidth = containerWidth / columnCount;

            // 如果平均列宽大于等于150px，说明不需要滚动，可以换行
            if (averageColumnWidth >= 150) {
              table.setAttribute('data-fits', 'true');
            } else {
              table.removeAttribute('data-fits');
            }
          }
        });
      };

      // 延迟执行，确保DOM已渲染
      setTimeout(checkTableFit, 100);

      // 监听窗口大小变化
      window.addEventListener('resize', checkTableFit);
      return () => window.removeEventListener('resize', checkTableFit);
    }
  }, [processedContent.updatedHtml]);

  const getMidShareContent = useMemo(() => {
    return <>{width <= 1150 && <SharePage shareUrl={currentHref} />}</>;
  }, [width, currentHref]);

  const getTopShareContent = useMemo(() => {
    return (
      <>
        {width > 1150 && (
          <div style={{ float: 'left', marginLeft: '-67px' }}>
            <Affix offsetTop={74}>
              <SharePage shareUrl={currentHref} />
            </Affix>
          </div>
        )}
      </>
    );
  }, [width, currentHref]);

  const getRecommendTagsContent = useMemo(() => {
    return (
      <>
        {recommendTags?.length > 0 && (
          <div className={styles.recommendTags}>
            <h2>{t('Read More')}</h2>
            <div className={styles.tagsContainer}>
              {(recommendTags ?? []).map((item: HomeArticleModel, index: number) => {
                return (
                  <div className={styles.linkContainer} key={item?.article_id}>
                    <span>{index + 1}.</span>
                    <Link className={styles.link} href={`/instagram-tracking-blog/${item?.title_id}`}>
                      {item.title}
                    </Link>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </>
    );
  }, [recommendTags]);

  const getArticleTopContent = useMemo(() => {
    return (
      <div className={styles.articleTopWrapper}>
        <div className={styles.time}>{dayjs(model?.create_time).format('YYYY-MM')}</div>
        {contentData?.book && <BookItem contentData={contentData} />}

        <h1 className={styles.title}>{contentData?.title ?? ' '}</h1>

        {model?.article_landings?.length > 0 && (
          <div className={styles.pageTagList}>
            {model?.article_landings?.map((item, index) => {
              return (
                <span
                  key={item?.name}
                  onClick={() => {
                    const tempPath = item?.url_path?.startsWith('http')
                      ? item?.url_path
                      : AppSettings.webHost + item?.url_path;
                    window.open(tempPath, '_blank');
                  }}
                >
                  {item?.name}
                </span>
              );
            })}
          </div>
        )}

        {contentData?.image_url && (
          <Image
            className={styles.image}
            src={imgUrl(contentData?.image_url, width > 576 ? 'big' : 'mid')}
            loading="lazy"
            alt={contentData?.image_text}
            layout="responsive"
            width={300}
            height={300}
          />
        )}
      </div>
    );
  }, [model?.create_time, model?.title, model?.article_landings, contentData, width]);

  return (
    <CommonBaseContent style={{ maxWidth: '1040px', minHeight: '100vh' }}>
      {getTopShareContent}

      <div className={styles.homeContent}>
        <div className={styles.leftContent} style={{ backgroundColor: '#fff' }}>
          <div className={styles.leftContainer}>
            <div className={styles.topSubCategory}>
              <span
                className={styles.link}
                onClick={() => {
                  router.push(`${AppSettings.webHost}`);
                }}
              >
                {t('Home')}
              </span>
              <span>{`>`}</span>
              <span
                className={styles.link}
                onClick={() => {
                  router.push(`/instagram-tracking-blog`);
                }}
              >
                {t('Blog')}
              </span>
              <span>{`>`}</span>
              <span
                className={styles.link}
                onClick={() => {
                  router.push(`/instagram-tracking-blog?classifies=${model?.type}`);
                }}
              >
                {model?.type}
              </span>
              <span>{`>`}</span>
              <span>{t('Article Details')}</span>
            </div>

            {contentData == undefined || contentData?.title == undefined ? (
              <>{LoadingSkeleton}</>
            ) : (
              <>
                {getArticleTopContent}
                <div className={styles.contentWrapper}>
                  {/* 文章描述区域 */}
                  {contentData?.introduction && (
                    <div className="text-lg text-muted-foreground mb-8 pb-8 border-b border-border leading-relaxed">
                      {contentData.introduction}
                    </div>
                  )}

                  {/* 目录 */}
                  {processedContent.h2Contents.length > 0 && (
                    <div className="bg-card rounded-lg border shadow-sm mb-8">
                      <div className="p-4 border-b border-border">
                        <div className="flex items-center gap-2">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            className="tabler-icon tabler-icon-list text-primary"
                          >
                            <path d="M9 6l11 0"></path>
                            <path d="M9 12l11 0"></path>
                            <path d="M9 18l11 0"></path>
                            <path d="M5 6l0 .01"></path>
                            <path d="M5 12l0 .01"></path>
                            <path d="M5 18l0 .01"></path>
                          </svg>
                          <h3 className="font-semibold text-foreground">{t('Table of Content')}</h3>
                        </div>
                      </div>
                      <nav className="py-2">
                        <ul className="space-y-0">
                          {processedContent.h2Contents.map((item: string, index: number) => (
                            <li key={index}>
                              <Link
                                className="flex items-center gap-3 py-2 px-4 text-sm transition-colors duration-200 hover:bg-muted/50 text-muted-foreground hover:text-foreground"
                                href={`/instagram-tracking-blog/${model?.title_id}#${index + 1}`}
                              >
                                <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-muted-foreground/40" />
                                <span className="flex-1 truncate font-medium">{item}</span>
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </nav>
                    </div>
                  )}

                  <BodyContent body={processedContent.updatedHtml} />
                  <ArticleVideoContent body={contentData?.body} />
                  {getRecommendTagsContent}
                  {getMidShareContent}
                </div>
              </>
            )}
          </div>

          <ArticleListContent id={model?.article_id} />
        </div>

        <RightSideContent />
      </div>

      {isManualScroll && model?.article_button && <BottomFlexButton model={model?.article_button} />}
    </CommonBaseContent>
  );
};

export default ArticleDetailUI;
